import RotatablePage, { type NormalizedRect, type PageHighlight } from "@/components/RotatablePage";
import { SOURCE_FIELDS, SOURCE_FIELD_LABELS, textInRect, locateExtractedValues, type ExtractedValue, type PageTextItem, type SourceField } from "@/lib/fieldLocations";
import SchedulePaymentDialog from "@/components/SchedulePaymentDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2, Plus, Trash2, AlertTriangle, CheckCircle2, Scissors, ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import PdfViewerToolbar, { type FitMode } from "@/components/PdfViewerToolbar";
import { downloadFilesAsZip } from "@/lib/pdfDownload";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import NumericInput from "@/components/NumericInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import type { ProcessedFile, VatLine } from "@/components/InvoiceList";
import { formatMoneyLike, formatNumber, parseNumericLike } from "@/lib/numberFormatting";
import ExpenseAccountCombobox, {
  type CatalogEntry,
  type HistoryEntry,
  type SuggestionEntry,
} from "@/components/ExpenseAccountCombobox";
import { loadCompanyAccountCatalog } from "@/lib/companyAccountCatalog";
import { loadSupplierMaster, OPERATION_TYPE_OPTIONS, RETENTION_TYPE_OPTIONS, type SupplierMasterEntry, type SupplierOperationType, type SupplierRetentionType } from "@/lib/supplierMaster";
import { loadAccountHistory, type AccountHistoryEntry } from "@/lib/accountHistory";
import QuickSupplierDialog, { type QuickSupplierCreated } from "@/components/QuickSupplierDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";


import { pdfjsLib, getPdfDocument } from "@/lib/pdfjs";

const fmt = (n: number | string) => formatNumber(n);

const normalizeNifLocal = (nif: string): string =>
  (nif || "").replace(/[\s.-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");
const normalizePlateLocal = (plate: string): string =>
  (plate || "").replace(/[\s-]/g, "").toUpperCase();

interface ProcessedFileSplitViewProps {
  file: ProcessedFile | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string, fields: { date: string; supplier: string; invoiceNumber: string; supplierTaxId?: string }) => void;
  onDownload: (file: ProcessedFile) => void;
  onUpdateNumericField?: (fileId: string, field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount", value: number | string, vatLineIndex?: number) => void;
  onAddVatLine?: (fileId: string) => void;
  onRemoveVatLine?: (fileId: string, vatLineIndex: number) => void;
  /** Empresa activa para cargar catálogo de cuentas / maestro / histórico. */
  appliedCompanyId?: string | null;
  /** Actualiza la cuenta de contrapartida de una línea (o file-level si vatLineIndex=0). */
  onUpdateExpenseAccount?: (fileId: string, account: string, vatLineIndex?: number) => void;
  onUpdateSupplierAccount?: (fileId: string, account: string) => void;
  onUpdateVatBookField?: (
    fileId: string,
    field: "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "paymentMethod",
    value: string,
  ) => void;
  onUpdateDescription?: (fileId: string, value: string) => void;
  onQuickSupplierCreated?: (fileId: string, created: QuickSupplierCreated) => void;
  /** Cuentas de pago configuradas para la empresa activa (caja/tarjeta). */
  paymentAccounts?: {
    cashAccount: string;
    cardAccount: string;
    cardAccounts?: { label: string; account: string }[];
  } | null;
  /** Cambia los datos del asiento de pago (generar sí/no, cuenta, tipo). */
  onUpdatePaymentEntry?: (
    fileId: string,
    fields: { generatePaymentEntry?: boolean; paymentEntryAccount?: string; paymentEntryKind?: "cash" | "card" },
  ) => void;
  /** Cambia el tipo de operación IVA (interior/intracom bienes/servicios/ISP/importación). */
  onUpdateOperationType?: (
    fileId: string,
    operationType: "interior" | "aib" | "ais" | "isp" | "importacion",
  ) => void;
  /** Cambia el tipo de retención (clasificación 111/115/180/agraria). */
  onUpdateRetentionType?: (
    fileId: string,
    retentionType: "none" | "mod111" | "mod115" | "mod193",
  ) => void;
  /** Cambia la deducibilidad del IVA de la factura completa. */
  onUpdateVatDeductibility?: (fileId: string, value: "full" | "none" | "half") => void;
  /** Marca/desmarca la factura como abono o rectificativa (invierte los importes). */
  onToggleCreditNote?: (fileId: string, value: boolean) => void;
  /** Abre el separador de PDF para este documento. Sólo relevante en PDFs multipágina. */
  onSplit?: (file: ProcessedFile) => void;
  onMergeWith?: (file: ProcessedFile, extra: File[], mode: "reprocess" | "annex") => void;
  /** Navegación entre documentos sin cerrar el visor. */
  onNavigate?: (direction: -1 | 1) => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  positionLabel?: string;
  /** Envía el documento a la papelera y cierra el visor. */
  onDiscard?: (fileId: string) => void;
  /** Actualiza el campo de observaciones libre del documento. */
  onUpdateNotes?: (fileId: string, value: string) => void;
  /** Series disponibles (la principal primero). Solo se muestra el selector si hay más de una. */
  seriesOptions?: { prefix: string; isMain: boolean }[];
  /** Cambia la serie de la factura; cadena vacía = serie principal. */
  onChangeSeries?: (fileId: string, prefix: string) => void;
  /** Anexos vinculados a la factura (no modifican el documento). */
  onUpdateAttachments?: (fileId: string, files: File[]) => void;
}

const ProcessedFileSplitView = ({
  file: pf,
  open,
  onClose,
  onSave,
  onDownload,
  onUpdateNumericField,
  onAddVatLine,
  onRemoveVatLine,
  appliedCompanyId,
  onUpdateExpenseAccount,
  onUpdateSupplierAccount,
  onUpdateVatBookField,
  onUpdateDescription,
  onQuickSupplierCreated,
  paymentAccounts,
  onUpdatePaymentEntry,
  onUpdateOperationType,
  onUpdateRetentionType,
  onUpdateVatDeductibility,
  onToggleCreditNote,
  onSplit,
  onMergeWith,
  onNavigate,
  hasPrev,
  hasNext,
  positionLabel,
  onDiscard,
  onUpdateNotes,
  seriesOptions = [],
  onChangeSeries,
  onUpdateAttachments,
}: ProcessedFileSplitViewProps) => {
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const { canAccessPayments } = useAuth();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("width");
  const viewerRef = useRef<HTMLDivElement>(null);
  const persistRef = useRef<() => void>(() => {});
  const [viewerH, setViewerH] = useState(0);
  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const upd = () => setViewerH(el.clientHeight - 32);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);
  const [showSources, setShowSources] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [activeField, setActiveField] = useState<SourceField>("supplierTaxId");
  const [pageTexts, setPageTexts] = useState<PageTextItem[][]>([]);

  const [editDate, setEditDate] = useState("");
  const [editSupplier, setEditSupplier] = useState("");
  const [editInvoiceNumber, setEditInvoiceNumber] = useState("");
  const [editTaxId, setEditTaxId] = useState("");

  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [master, setMaster] = useState<Map<string, SupplierMasterEntry> | null>(null);
  const [history, setHistory] = useState<Map<string, AccountHistoryEntry[]>>(new Map());
  const [quickSupplierOpen, setQuickSupplierOpen] = useState(false);
  const [operationSelectOpen, setOperationSelectOpen] = useState(false);
  const [deductibilitySelectOpen, setDeductibilitySelectOpen] = useState(false);
  const { toast } = useToast();


  // Load catalog / master / history when dialog opens for a given company
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [m, h, c] = await Promise.all([
          loadSupplierMaster(appliedCompanyId ?? undefined),
          loadAccountHistory(appliedCompanyId ?? null),
          appliedCompanyId ? loadCompanyAccountCatalog(appliedCompanyId) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setMaster(m);
        setHistory(h);
        setCatalog((c as Array<{ account: string; description: string }>).map((r) => ({
          account: r.account,
          description: r.description,
        })));
      } catch (e) {
        console.warn("[SplitView] Error loading account data:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, appliedCompanyId]);

  // Sync local state when file changes
  useEffect(() => {
    if (pf && open) {
      setEditDate(pf.date);
      setEditSupplier(pf.supplier);
      setEditInvoiceNumber(pf.invoiceNumber);
      setEditTaxId(pf.supplierTaxId || "");
      setRotation(0);
      // Reset per-document view options so they don't carry over to the next PDF
      setShowSources(false);
      setSelectMode(false);
    }
  }, [pf?.id, open]);

  // Render PDF/image
  useEffect(() => {
    if (!pf || !open) {
      setPdfPages([]);
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const isImage =
      pf.file.type === "image/png" ||
      pf.file.type === "image/jpeg" ||
      /\.(png|jpe?g)$/i.test(pf.file.name);

    // Limpiamos siempre el documento anterior antes de pintar el nuevo.
    setPdfPages([]);
    setPageTexts([]);
    setImageUrl(null);

    if (isImage) {
      setLoading(false);
      objectUrl = URL.createObjectURL(pf.file);
      setImageUrl(objectUrl);
    } else {
      setLoading(true);
      (async () => {
        try {
          const buf = await pf.file.arrayBuffer();
          if (cancelled) return;
          const pdf = await (await getPdfDocument({ data: buf })).promise;
          const pages: string[] = [];
          const texts: PageTextItem[][] = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            if (cancelled) return;
            const page = await pdf.getPage(i);
            const vp = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement("canvas");
            canvas.width = vp.width;
            canvas.height = vp.height;
            await page.render({
              canvas, canvasContext: canvas.getContext("2d")!,
              viewport: vp,
            }).promise;
            pages.push(canvas.toDataURL("image/png"));
            try {
              const tc = await page.getTextContent();
              const items: PageTextItem[] = [];
              for (const it of tc.items as any[]) {
                if (!it.str || !it.transform) continue;
                const [, , , , e, f] = it.transform;
                const h = it.height || Math.abs(it.transform[3]) || 8;
                const [x1, y1] = vp.convertToViewportPoint(e, f);
                const [x2, y2] = vp.convertToViewportPoint(e + (it.width || 0), f + h);
                items.push({
                  str: it.str,
                  left: Math.min(x1, x2) / vp.width,
                  right: Math.max(x1, x2) / vp.width,
                  top: Math.min(y1, y2) / vp.height,
                  bottom: Math.max(y1, y2) / vp.height,
                });
              }
              texts.push(items);
            } catch {
              texts.push([]);
            }
            canvas.width = 0;
            canvas.height = 0;
          }
          if (!cancelled) { setPdfPages(pages); setPageTexts(texts); }
        } catch (e) {
          console.error("Error rendering PDF in split view:", e);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pf?.id, open]);

  const handleNumericChange = useCallback(
    (field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount", rawValue: string | number, vatLineIndex?: number) => {
      if (!pf) return;
      onUpdateNumericField?.(pf.id, field, rawValue, vatLineIndex);
    },
    [pf, onUpdateNumericField],
  );

  // Atajos de teclado dentro del split view (alta proveedor: Alt+N o Ctrl+M)
  useEffect(() => {
    if (!open || !pf) return;
    const handler = (e: KeyboardEvent) => {
      const isAltN = e.altKey && !e.ctrlKey && !e.metaKey && (e.key === "n" || e.key === "N");
      const isCtrlM = (e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "m" || e.key === "M");
      if (isAltN || isCtrlM) {
        e.preventDefault();
        e.stopPropagation();
        setQuickSupplierOpen(true);
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, pf]);

  useEffect(() => {
    if (!open || !onNavigate) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      const editable = t?.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      if (editable) return;
      if (e.altKey && e.key === "ArrowLeft" && hasPrev) { e.preventDefault(); persistRef.current(); onNavigate(-1); }
      if (e.altKey && e.key === "ArrowRight" && hasNext) { e.preventDefault(); persistRef.current(); onNavigate(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onNavigate, hasPrev, hasNext]);

  // El useMemo debe ejecutarse SIEMPRE (antes del retorno temprano) para no romper el orden de hooks.
  const extractedMatches = useMemo(() => {
    if (!pf || !pageTexts.length) return [];
    const vals: ExtractedValue[] = [
      { key: "date", label: SOURCE_FIELD_LABELS.date, value: pf.date, kind: "date" },
      { key: "supplierTaxId", label: SOURCE_FIELD_LABELS.supplierTaxId, value: pf.supplierTaxId, kind: "taxId" },
      { key: "supplier", label: SOURCE_FIELD_LABELS.supplier, value: pf.supplier, kind: "text" },
      { key: "invoiceNumber", label: SOURCE_FIELD_LABELS.invoiceNumber, value: pf.invoiceNumber, kind: "text" },
      { key: "totalAmount", label: SOURCE_FIELD_LABELS.totalAmount, value: pf.totalAmount, kind: "amount" },
      { key: "irpf", label: "IRPF", value: pf.irpfAmount, kind: "amount" },
    ];
    (pf.vatBreakdown || []).forEach((l, i) => {
      vals.push({ key: `base${i}`, label: `Base ${l.vatRate}%`, value: l.taxableBase, kind: "amount" });
      vals.push({ key: `iva${i}`, label: `IVA ${l.vatRate}%`, value: l.vatAmount, kind: "amount" });
    });
    return locateExtractedValues(pageTexts, vals);
  }, [pf, pageTexts]);

  if (!pf) return null;

  const persistEdits = () => {
    if (!editDate || !editSupplier || !editInvoiceNumber) return;
    const taxId = editTaxId.trim() || undefined;
    if (editDate === pf.date && editSupplier === pf.supplier && editInvoiceNumber === pf.invoiceNumber && taxId === (pf.supplierTaxId || undefined)) return;
    onSave(pf.id, { date: editDate, supplier: editSupplier, invoiceNumber: editInvoiceNumber, supplierTaxId: taxId });
  };
  persistRef.current = persistEdits;
  const handleClose = () => { persistEdits(); onClose(); };
  const handleDownloadAll = () => {
    const att = pf.attachments ?? [];
    if (att.length === 0) return onDownload(pf);
    const base = pf.newName.replace(/\.[^.]+$/, "");
    void downloadFilesAsZip([{ file: pf.file, name: pf.newName }, ...att.map((f) => ({ file: f, name: `${base}_anexo_${f.name}` }))], `${base}.zip`);
  };

  const canShowSources = !!pf?.fieldLocations || extractedMatches.length > 0;

  const highlightsFor = (pageNo: number): PageHighlight[] | undefined => {
    if (!showSources || !pf) return undefined;
    const out: PageHighlight[] = [];
    const found = new Set<string>();
    extractedMatches.forEach((m, i) => {
      found.add(m.key);
      if (m.page === pageNo) out.push({ ...m, key: `${m.key}-${i}`, active: m.key === activeField });
    });
    for (const k of SOURCE_FIELDS) {
      if (found.has(k)) continue;
      // En PDFs con texto no pintar cajas de IA que no se han podido verificar.
      if (pageTexts.some((items) => items.some((item) => item.str.trim()))) continue;
      const loc = pf.fieldLocations?.[k];
      // En escaneados, descartar zonas extensas que abarcan párrafos o leyendas.
      if (loc && loc.page === pageNo && loc.bottom - loc.top <= 0.05
        && (loc.right - loc.left) * (loc.bottom - loc.top) <= 0.025) {
        out.push({ key: k, label: SOURCE_FIELD_LABELS[k], ...loc, active: k === activeField });
      }
    }
    return out;
  };

  const handleSelectRect = (pageIdx: number, rect: NormalizedRect) => {
    if (!pf) return;
    const raw = textInRect(pageTexts[pageIdx] || [], rect);
    if (!raw) {
      toast({
        title: "No hay texto seleccionable",
        description: "Este documento es una imagen escaneada: escribe el dato a mano.",
      });
      return;
    }
    const label = SOURCE_FIELD_LABELS[activeField];
    switch (activeField) {
      case "supplierTaxId":
        setEditTaxId(raw.replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, ""));
        break;
      case "invoiceNumber":
        setEditInvoiceNumber(raw);
        break;
      case "supplier":
        setEditSupplier(raw);
        break;
      case "date": {
        const m = raw.match(/(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})/);
        if (m) {
          const y = m[3].length === 2 ? `20${m[3]}` : m[3];
          setEditDate(`${y}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`);
        } else setEditDate(raw);
        break;
      }
      case "totalAmount": {
        const n = parseNumericLike(raw.replace(/[^0-9,.\-]/g, ""));
        if (!Number.isFinite(n) || n === 0) {
          toast({ title: "No se reconoce un importe", description: raw });
          return;
        }
        handleNumericChange("totalAmount", n);
        break;
      }
    }
    toast({ title: `${label} rellenado desde el PDF`, description: raw });
    setSelectMode(false);
  };

  const fieldFocus = (f: SourceField) => ({ onFocusCapture: () => { setActiveField(f); if (pdfPages.length) { setRotation(0); setSelectMode(true); } } });
  const fieldRing = (_f: SourceField) => "";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") persistEdits();
  };


  const breakdown = pf.vatBreakdown && pf.vatBreakdown.length > 0
    ? pf.vatBreakdown
    : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];

  const sumBase = breakdown.reduce((s, l) => s + l.taxableBase, 0);
  const sumVat = breakdown.reduce((s, l) => s + l.vatAmount, 0);
  const irpfAmt = pf.irpfAmount ?? 0;
  const calculatedTotal = Math.round((sumBase + sumVat - irpfAmt) * 100) / 100;
  const totalAmount = pf.totalAmount ?? 0;
  const totalDiff = Math.round((totalAmount - calculatedTotal) * 100) / 100;
  const hasInconsistency = totalAmount > 0 && Math.abs(totalDiff) > 0.05;
  // Descuadre de redondeo del emisor (1-2 céntimos): se puede cuadrar con un clic.
  const canRoundingFix = totalAmount > 0 && Math.abs(totalDiff) > 0 && Math.abs(totalDiff) <= 0.02;
  const largestBaseIndex = breakdown.reduce(
    (best, line, i) => (line.taxableBase > (breakdown[best]?.taxableBase ?? -Infinity) ? i : best),
    0,
  );

  // Precompute suggestions/history shared by all VAT lines (depend on supplier, not on line)
  const lineSuggestions: SuggestionEntry[] = (() => {
    const out: SuggestionEntry[] = [];
    if (!master || !pf.supplierTaxId) return out;
    const entry = master.get(normalizeNifLocal(pf.supplierTaxId));
    if (!entry) return out;
    const seen = new Set<string>();
    const plate = pf.licensePlate ? normalizePlateLocal(pf.licensePlate) : "";
    if (plate) {
      for (const cat of catalog) {
        if (seen.has(cat.account)) continue;
        if ((cat.description || "").toUpperCase().includes(plate)) {
          out.push({ account: cat.account, description: cat.description, matchesPlate: true });
          seen.add(cat.account);
        }
      }
    }
    if (entry.accountCode && !seen.has(entry.accountCode)) {
      const cm = catalog.find((c) => c.account === entry.accountCode);
      out.push({
        account: entry.accountCode,
        description: cm?.description ?? entry.description,
        matchesPlate: false,
      });
    }
    return out;
  })();
  const lineHistory = (history.get(normalizeNifLocal(pf.supplierTaxId || "")) || []).map((h) => {
    const cm = catalog.find((c) => c.account === h.account);
    return cm ? { ...h, description: cm.description } : h;
  });

  const multiLine = breakdown.length > 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh] overflow-hidden flex flex-col p-0"
        onPointerDownOutside={(e) => {
          if (quickSupplierOpen) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (quickSupplierOpen) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <DialogTitle className="truncate text-sm">
              {pf.newName}
            </DialogTitle>
            {pdfPages.length > 0 && (
              <span className="shrink-0 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {pdfPages.length} {pdfPages.length === 1 ? "página" : "páginas"}
              </span>
            )}
            <Badge
              variant={hasInconsistency ? "destructive" : "default"}
              className={`text-[10px] px-1.5 py-0 shrink-0 gap-1 ${
                hasInconsistency
                  ? ""
                  : "bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(152,40%,45%)]"
              }`}
            >
              {hasInconsistency ? (
                <><AlertTriangle className="h-3 w-3" /> <span>Descuadrado</span></>
              ) : (
                <><CheckCircle2 className="h-3 w-3" /> <span>Cuadrado</span></>
              )}
            </Badge>
            {onChangeSeries && seriesOptions.length > 1 && (() => {
              const main = seriesOptions.find((o) => o.isMain);
              const own = (pf.seriesPrefix ?? "").toUpperCase();
              const current = own && own !== (main?.prefix ?? "") ? own : (main?.prefix ?? "");
              return (
                <div
                  role="tablist"
                  aria-label="Serie de la factura"
                  className="ml-auto mr-8 flex shrink-0 items-center gap-1 rounded-lg border-2 border-primary/40 bg-primary/5 p-0.5"
                >
                  <span className="px-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Serie</span>
                  {seriesOptions.map((o) => {
                    const active = o.prefix === current;
                    return (
                      <button
                        key={o.prefix || "_main"}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => !active && onChangeSeries(pf.id, o.isMain ? "" : o.prefix)}
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold transition-colors ${
                          active ? "bg-primary text-primary-foreground" : "text-primary hover:bg-primary/10"
                        }`}
                      >
                        {o.isMain ? (o.prefix || "Principal") : o.prefix}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogDescription className="sr-only">
            Editor de factura procesada con vista dividida documento-formulario.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden min-h-0">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Left: Document viewer */}
            <ResizablePanel defaultSize={55} minSize={25}>
              <div className="relative h-full flex flex-col">
                <div className="flex items-center justify-end gap-1 px-3 py-1.5 border-b bg-muted/30">
                  <PdfViewerToolbar
                    onDownload={handleDownloadAll}
                    showSources={showSources}
                    onToggleSources={() => {
                      if (!showSources && !canShowSources) {
                        toast({
                          title: "No se han localizado datos en el documento",
                          description: "No se puede situar dónde se leyó cada dato (suele ocurrir en escaneados antiguos).",
                        });
                        return;
                      }
                      setShowSources((v) => !v);
                    }}
                    zoom={zoom}
                    onZoom={setZoom}
                    fitMode={fitMode}
                    onFitMode={setFitMode}
                    onRotate={() => setRotation((r) => (r + 270) % 360)}
                    mainFile={pf.file}
                    onSplit={onSplit && pdfPages.length > 1 ? () => onSplit(pf) : undefined}
                    onMerge={onMergeWith ? (extra, mode) => onMergeWith(pf, extra, mode) : undefined}
                    onDelete={onDiscard ? () => { onDiscard(pf.id); onClose(); } : undefined}
                  />
                </div>
                <div ref={viewerRef} className="flex-1 overflow-auto p-4 space-y-3 flex flex-col items-center">
                  {loading && (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Cargando documento…</span>
                    </div>
                  )}
                  {selectMode && (
                    <div className="w-full rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs text-foreground">
                      Arrastra sobre el documento para copiar el texto en <strong>{SOURCE_FIELD_LABELS[activeField]}</strong>. Haz clic en otra casilla para cambiar de destino. <button type="button" className="underline ml-1" onClick={() => setSelectMode(false)}>Cancelar</button>
                    </div>
                  )}
                  {imageUrl && (
                    <RotatablePage src={imageUrl} alt={pf.file.name} rotation={rotation} zoom={zoom} fitMode={fitMode} fitHeight={viewerH} highlights={highlightsFor(1)} />
                  )}
                  {pdfPages.map((src, i) => (
                    <RotatablePage
                      key={i}
                      src={src}
                      alt={`Página ${i + 1}`}
                      rotation={rotation}
                      zoom={zoom}
                      fitMode={fitMode}
                      fitHeight={viewerH}
                      highlights={highlightsFor(i + 1)}
                      selectable={selectMode}
                      onSelectRect={(r) => handleSelectRect(i, r)}
                    />
                  ))}
                </div>
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            {/* Right: Edit form */}
             <ResizablePanel defaultSize={45} minSize={25}>
               <div className="h-full overflow-y-auto p-4 space-y-4 flex flex-col">
                 {onNavigate && (
                   <div className="order-last sticky -bottom-4 z-10 -mx-4 -mb-4 flex shrink-0 justify-end border-t bg-background px-4 pt-2 pb-3">
                     <div className="flex items-center gap-1 rounded-md border bg-background p-1 shadow-md">
                       <Button
                         type="button"
                         variant="outline"
                         size="sm"
                         className="h-7 px-2"
                         onClick={() => { persistEdits(); onNavigate(-1); }}
                         disabled={!hasPrev}
                         title="Documento anterior (Alt + ←)"
                       >
                         <ChevronLeft className="h-4 w-4" />
                       </Button>
                       {positionLabel && (
                         <span className="text-[11px] text-muted-foreground tabular-nums px-1">
                           {positionLabel}
                         </span>
                       )}
                       <Button
                         type="button"
                         variant="outline"
                         size="sm"
                         className="h-7 px-2"
                         onClick={() => { persistEdits(); onNavigate(1); }}
                         disabled={!hasNext}
                         title="Documento siguiente (Alt + →)"
                       >
                         <ChevronRight className="h-4 w-4" />
                       </Button>
                     </div>
                   </div>
                 )}
                 {/* Info summary */}
                <div className="rounded-md bg-accent/50 px-3 py-2 text-xs text-muted-foreground">
                  <span>Original: </span><span className="font-medium text-foreground">{pf.originalName}</span>
                </div>
                {canAccessPayments && appliedCompanyId && (
                  <div className="flex justify-end">
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setScheduleOpen(true)}>
                      <CalendarClock className="h-3.5 w-3.5 mr-1" /> Agendar vencimiento
                    </Button>
                    <SchedulePaymentDialog
                      open={scheduleOpen}
                      onOpenChange={setScheduleOpen}
                      companyId={appliedCompanyId}
                      source="received_pdf"
                      prefill={{
                        supplier_name: pf.supplier,
                        supplier_tax_id: pf.supplierTaxId,
                        invoice_number: pf.invoiceNumber,
                        invoice_date: pf.date,
                        amount: pf.totalAmount,
                        file: pf.file,
                      }}
                    />
                  </div>
                )}

                {/* Editable fields */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Datos de la factura
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    <div className={`space-y-1 ${fieldRing("date")}`} {...fieldFocus("date")}>
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Fecha
                      </label>
                      <Input
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="YYYY-MM-DD"
                        className="h-8 text-xs"
                        autoFocus
                      />
                    </div>
                    <div className={`space-y-1 ${fieldRing("supplierTaxId")}`} {...fieldFocus("supplierTaxId")}>
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        NIF / CIF
                      </label>
                      <Input
                        value={editTaxId}
                        onChange={(e) => setEditTaxId(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="B12345678"
                        className="h-8 text-xs font-mono"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Alta de proveedor: Alt+N o Ctrl+M
                      </p>
                    </div>


                    <div className={`space-y-1 ${fieldRing("supplier")}`} {...fieldFocus("supplier")}>
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Proveedor
                      </label>
                      <Input
                        value={editSupplier}
                        onChange={(e) => setEditSupplier(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className={`space-y-1 ${fieldRing("invoiceNumber")}`} {...fieldFocus("invoiceNumber")}>
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Nº Factura
                      </label>
                      <Input
                        value={editInvoiceNumber}
                        onChange={(e) => setEditInvoiceNumber(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Tipo de operación IVA (interior / intracom / ISP / importación) */}
                {onUpdateOperationType && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Tipo de operación IVA
                    </h3>
                    <Select
                      open={operationSelectOpen}
                      onOpenChange={setOperationSelectOpen}
                      value={pf.operationType ?? "interior"}
                      onValueChange={(v) => {
                        setOperationSelectOpen(false);
                        const id = pf.id;
                        queueMicrotask(() => onUpdateOperationType(id, v as SupplierOperationType));
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATION_TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-xs">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      Intracom./ISP generan en A3 el doble asiento 477/472 automáticamente; AIB/AIS alimentan además el modelo 349.
                    </p>
                  </div>
                )}

                 {/* Abono / rectificativa: solo aviso, sin interruptor */}
                 {pf.isCreditNote && (
                   <p className="text-[11px] font-medium text-destructive">
                     Abono / rectificativa: importes en negativo
                     {pf.creditNoteHints && pf.creditNoteHints.length > 0
                       ? ` (${pf.creditNoteHints.join(", ")})`
                       : ""}
                     .
                   </p>
                 )}

                 {/* Deducibilidad del IVA soportado (por factura completa) */}
                 {onUpdateVatDeductibility && (
                   <div className="space-y-2">
                     <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                       Deducibilidad del IVA
                     </h3>
                     <Select
                       open={deductibilitySelectOpen}
                       onOpenChange={setDeductibilitySelectOpen}
                       value={pf.vatDeductibility ?? "full"}
                       onValueChange={(v) => {
                         setDeductibilitySelectOpen(false);
                         const id = pf.id;
                         queueMicrotask(() => onUpdateVatDeductibility(id, v as "full" | "none" | "half"));
                       }}
                     >
                       <SelectTrigger className="h-8 text-xs">
                         <SelectValue />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="full" className="text-xs">Deducible</SelectItem>
                         <SelectItem value="none" className="text-xs">No deducible 100 %</SelectItem>
                         <SelectItem value="half" className="text-xs">Deducible al 50 %</SelectItem>
                       </SelectContent>
                     </Select>
                     <p className="text-[10px] text-muted-foreground">
                       No deducible → subtipo 02 en el SUENLACE. Al 50 % cada tramo se parte en dos
                       líneas (mitad deducible, mitad no). Solo aplica a operaciones interiores.
                     </p>
                   </div>
                 )}



                 {/* Cuenta de contrapartida (solo cuando hay una única línea de IVA) */}
                 {onUpdateExpenseAccount && !multiLine && (
                   <div className="space-y-2">
                     <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                       Cuenta de contrapartida
                     </h3>
                     <ExpenseAccountCombobox
                       value={pf.expenseAccountCode || ""}
                       suggestions={lineSuggestions}
                       history={lineHistory}
                       catalog={catalog}
                       onChange={(account) => {
                         onUpdateExpenseAccount?.(pf.id, account, 0);
                       }}
                       placeholder="Seleccionar cuenta…"
                       triggerClassName="h-8 max-w-full"
                       companyId={appliedCompanyId ?? null}
                       onAccountCreated={(account, description) =>
                         setCatalog((prev) =>
                           prev.some((c) => c.account === account)
                             ? prev
                             : [...prev, { account, description }],
                         )
                       }
                     />
                   </div>
                 )}


                 {/* Dynamic VAT breakdown */}
                 <div className="space-y-2">
                   <div className="flex items-center justify-between">
                     <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                       Desglose IVA{multiLine ? " y contrapartidas" : ""}
                     </h3>
                     <Button
                       type="button"
                       variant="ghost"
                       size="sm"
                       className="h-6 px-2 text-xs gap-1"
                       onClick={() => onAddVatLine?.(pf.id)}
                     >
                       <Plus className="h-3 w-3" /> <span>Añadir línea</span>
                     </Button>
                   </div>
                   <div className="rounded-md border text-xs">
                     <div className="grid grid-cols-[1fr_80px_1fr_32px] gap-1 border-b bg-muted/50 px-2 py-1.5 font-medium text-muted-foreground">
                       <span>Base imp.</span>
                       <span className="text-center">% IVA</span>
                       <span className="text-right">Cuota</span>
                       <span />
                     </div>
                     {breakdown.map((line, i) => (
                       <VatLineRow
                         key={`${pf.id}-vat-${i}`}
                         line={line}
                         index={i}
                         canRemove={breakdown.length > 1}
                         onChange={handleNumericChange}
                         onRemove={() => onRemoveVatLine?.(pf.id, i)}
                         showAccount={multiLine && !!onUpdateExpenseAccount}
                         accountValue={line.expenseAccount || ""}
                         accountSuggestions={lineSuggestions}
                         accountHistory={lineHistory}
                         accountCatalog={catalog}
                         accountCompanyId={appliedCompanyId ?? null}
                         onAccountCreated={(account, description) =>
                           setCatalog((prev) =>
                             prev.some((c) => c.account === account)
                               ? prev
                               : [...prev, { account, description }],
                           )
                         }
                         onAccountChange={(account) => onUpdateExpenseAccount?.(pf.id, account, i)}
                       />
                     ))}
                   </div>
                 </div>

                 {/* IRPF */}
                 <div className="space-y-2">
                   <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                     Retenciones (IRPF)
                   </h3>
                   <div className="grid grid-cols-2 gap-2">
                     <div className="space-y-1">
                       <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                         % IRPF
                       </label>
                       <NumericInput
                         value={pf.irpfRate ?? 0}
                         onChange={(v) => handleNumericChange("irpfRate", v)}
                         className="h-7 text-xs text-right"
                       />
                     </div>
                     <div className="space-y-1">
                       <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                         Cuota IRPF
                       </label>
                       <NumericInput
                         value={pf.irpfAmount ?? 0}
                         onChange={(v) => handleNumericChange("irpfAmount", v)}
                         className="h-7 text-xs text-right"
                       />
                     </div>
                   </div>
                   {onUpdateRetentionType && (
                     <div className="space-y-1">
                       <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2">
                         <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                           Tipo de retención
                         </label>
                         <Select
                           value={pf.retentionType ?? "none"}
                           onValueChange={(v) =>
                             onUpdateRetentionType(pf.id, v as SupplierRetentionType)
                           }
                         >
                           <SelectTrigger className="h-7 min-w-0 text-xs">
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent>
                             {RETENTION_TYPE_OPTIONS.map((o) => (
                               <SelectItem key={o.value} value={o.value} className="text-xs">
                                 {o.label}
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>
                       </div>
                     </div>
                   )}
                 </div>

                {/* Asiento de pago (SUENLACE) */}
                {onUpdatePaymentEntry && (paymentAccounts?.cashAccount || paymentAccounts?.cardAccount || (paymentAccounts?.cardAccounts && paymentAccounts.cardAccounts.length > 0) || pf.generatePaymentEntry) && (
                  <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Asiento de pago en SUENLACE
                      </h3>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={!!pf.generatePaymentEntry}
                          onChange={(e) => onUpdatePaymentEntry(pf.id, { generatePaymentEntry: e.target.checked })}
                        />
                        <span>Generar</span>
                      </label>
                    </div>
                    {pf.generatePaymentEntry && (
                      <div className="space-y-2">
                        {paymentAccounts?.cardAccounts && paymentAccounts.cardAccounts.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {paymentAccounts.cashAccount && (
                              <button
                                type="button"
                                onClick={() => onUpdatePaymentEntry(pf.id, { paymentEntryAccount: paymentAccounts.cashAccount, paymentEntryKind: "cash" })}
                                className={`px-2 py-0.5 rounded text-[10px] border ${pf.paymentEntryAccount === paymentAccounts.cashAccount ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                              >
                                Caja · {paymentAccounts.cashAccount}
                              </button>
                            )}
                            {paymentAccounts.cardAccounts.map((c, i) => (
                              <button
                                key={`${c.account}-${i}`}
                                type="button"
                                onClick={() => onUpdatePaymentEntry(pf.id, { paymentEntryAccount: c.account, paymentEntryKind: "card" })}
                                className={`px-2 py-0.5 rounded text-[10px] border ${pf.paymentEntryAccount === c.account ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                              >
                                {c.label || "Tarjeta"} · {c.account}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                          <Input
                            value={pf.paymentEntryAccount || ""}
                            onChange={(e) => onUpdatePaymentEntry(pf.id, { paymentEntryAccount: e.target.value })}
                            placeholder="Cuenta de contrapartida (570, 5525…)"
                            className="h-7 text-xs font-mono"
                          />
                          {pf.paymentEntryKind && (
                            <Badge variant="secondary" className="text-[10px]">
                              {pf.paymentEntryKind === "cash" ? "Caja" : "Tarjeta"}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {pf.isCreditNote || totalAmount < 0
                        ? <>Al ser un abono, se añade el apunte invertido: Debe {pf.paymentEntryAccount || "cuenta de pago"} / Haber proveedor, con concepto DEVOLUCIÓN FRA/...</>
                        : <>Se añade al SUENLACE un 2º apunte: Debe proveedor / Haber {pf.paymentEntryAccount || "cuenta de pago"} por el total de la factura.</>}
                    </p>
                  </div>
                )}

                {/* Total */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Total factura
                  </h3>
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div className={`space-y-1 ${fieldRing("totalAmount")}`} {...fieldFocus("totalAmount")}>
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Total
                      </label>
                      <NumericInput
                        value={totalAmount}
                        onChange={(v) => handleNumericChange("totalAmount", v)}
                        className={`h-7 text-xs text-right ${hasInconsistency ? "text-destructive border-destructive" : ""}`}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground pb-1 space-y-0.5">
                      <div>
                        <span>Suma calculada: </span>
                        <span className={`font-medium ${hasInconsistency ? "text-destructive" : "text-foreground"}`}>{fmt(calculatedTotal)} €</span>
                      </div>
                      <div>
                        <span>Descuadre: </span>
                        <span className={`font-medium ${hasInconsistency ? "text-destructive" : totalDiff !== 0 ? "text-amber-600" : "text-emerald-600"}`}>
                          {fmt(totalDiff)} €
                        </span>
                        {hasInconsistency && (
                          <span className="inline-flex items-center gap-1 ml-1 text-destructive">
                            <AlertTriangle className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                      {canRoundingFix && (
                        <button
                          type="button"
                          className="text-[10px] underline text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            handleNumericChange(
                              "taxableBase",
                              Math.round(((breakdown[largestBaseIndex]?.taxableBase ?? 0) + totalDiff) * 100) / 100,
                              largestBaseIndex,
                            )
                          }
                        >
                          Ajustar {fmt(totalDiff)} € en la base mayor
                        </button>
                      )}
                </div>

                {/* Observaciones — nota interna que se estampa como post-it sobre el PDF */}
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Observaciones
                  </label>
                  <Textarea
                    value={pf.notes ?? ""}
                    onChange={(e) => onUpdateNotes?.(pf.id, e.target.value.slice(0, 60))}
                    maxLength={60}
                    rows={2}
                    placeholder="Nota interna · se estampará como post-it en la esquina superior derecha"
                    className="text-xs resize-none"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Post-it amarillo translúcido · no tapa los datos de la factura
                  </p>
                  <p className="text-[10px] text-muted-foreground text-right">
                    {60 - (pf.notes?.length ?? 0)} caracteres restantes
                  </p>
                </div>

                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">

                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
        <QuickSupplierDialog
          open={quickSupplierOpen}
          onClose={() => setQuickSupplierOpen(false)}
          companyId={appliedCompanyId ?? null}
          defaults={{ taxId: editTaxId || pf.supplierTaxId || "", name: editSupplier || pf.supplier || "" }}
          catalog={catalog}
          contained
          onCreated={(created: QuickSupplierCreated) => {
            setMaster((prev) => {
              const next = new Map(prev || new Map<string, SupplierMasterEntry>());
              next.set(created.taxId, created.entry);
              return next;
            });
            setEditTaxId(created.taxId);
            if (created.entry.description) {
              setEditSupplier(created.entry.description);
            }
            if (onQuickSupplierCreated) {
              onQuickSupplierCreated(pf.id, created);
            } else {
              if (created.entry.description) {
                onUpdateVatBookField?.(pf.id, "supplier", created.entry.description);
              }
              onUpdateVatBookField?.(pf.id, "supplierTaxId", created.taxId);
              onUpdateSupplierAccount?.(pf.id, created.accountCode);
              if (created.expenseAccount) {
                onUpdateExpenseAccount?.(pf.id, created.expenseAccount, 0);
              }
              if (created.description) {
                onUpdateDescription?.(pf.id, created.description);
              }
              if (created.entry.operationType && !pf.manualOperationType) {
                onUpdateOperationType?.(pf.id, created.entry.operationType);
              }
              if (created.entry.retentionType) {
                onUpdateRetentionType?.(pf.id, created.entry.retentionType);
              }
            }
            setQuickSupplierOpen(false);
            toast({ title: "Proveedor dado de alta", description: `${created.taxId} añadido al maestro.` });
          }}
        />
      </DialogContent>
    </Dialog>
  );
};


/** Individual VAT line row with controlled inputs */
const VatLineRow = ({
  line,
  index,
  canRemove,
  onChange,
  onRemove,
  showAccount = false,
  accountValue = "",
  accountSuggestions = [],
  accountHistory = [],
  accountCatalog = [],
  accountCompanyId = null,
  onAccountCreated,
  onAccountChange,
}: {
  line: VatLine;
  index: number;
  canRemove: boolean;
  onChange: (field: "taxableBase" | "vatRate" | "vatAmount", rawValue: string, vatLineIndex: number) => void;
  onRemove: () => void;
  showAccount?: boolean;
  accountValue?: string;
  accountSuggestions?: SuggestionEntry[];
  accountHistory?: HistoryEntry[];
  accountCatalog?: CatalogEntry[];
  accountCompanyId?: string | null;
  onAccountCreated?: (account: string, description: string) => void;
  onAccountChange?: (account: string) => void;
}) => {
  return (
    <div className={showAccount ? "border-b last:border-b-0 px-2 py-1.5 space-y-1" : "px-0 py-0"}>
      <div className={`grid grid-cols-[1fr_80px_1fr_32px] gap-1 items-center ${showAccount ? "" : "px-2 py-1"}`}>
        <NumericInput
          value={line.taxableBase}
          onChange={(v) => onChange("taxableBase", v, index)}
          className="h-7 text-xs text-right"
        />
        <NumericInput
          value={line.vatRate}
          onChange={(v) => onChange("vatRate", v, index)}
          className="h-7 text-xs text-right"
        />
        <NumericInput
          value={line.vatAmount}
          onChange={(v) => onChange("vatAmount", v, index)}
          className="h-7 text-xs text-right"
        />
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Eliminar línea"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span />
        )}
      </div>
      {showAccount && onAccountChange && (
        <div className="flex items-center gap-2 pl-1">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
            Contrapartida
          </span>
          <ExpenseAccountCombobox
            value={accountValue}
            suggestions={accountSuggestions}
            history={accountHistory}
            catalog={accountCatalog}
            onChange={onAccountChange}
            placeholder="Seleccionar cuenta…"
            triggerClassName="h-7 max-w-full flex-1 text-xs"
            companyId={accountCompanyId}
            onAccountCreated={onAccountCreated}
          />
        </div>
      )}
    </div>
  );
};

export default ProcessedFileSplitView;
